import { memo, type ReactNode } from "react";
import type { TokenId } from "@/model/tokens";
import { cn } from "@/ui/kit/cn";
import { C } from "./tok";
import { v } from "./vars";

const k = (key: string) => `claude.${key}` as TokenId;

function Box({
  border,
  children,
  className,
}: {
  border: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn("whitespace-pre rounded-md border px-[1ch] py-[0.2lh]", className)}
      style={{ borderColor: v(k(border)) }}
      data-token={k(border)}
    >
      {children}
    </div>
  );
}

function Tool({ name, arg, children }: { name: string; arg: string; children?: ReactNode }) {
  return (
    <div>
      <div className="whitespace-pre">
        <C fg={k("success")}>●</C>{" "}
        <C fg={k("text")} bold>
          {name}
        </C>
        <C fg={k("text")}>({arg})</C>
      </div>
      {children && (
        <div className="flex whitespace-pre">
          <C fg={k("inactive")}>{"  └  "}</C>
          <div className="min-w-0 flex-1">{children}</div>
        </div>
      )}
    </div>
  );
}

function DiffLine({
  n,
  kind,
  before,
  word,
  after,
}: {
  n: number;
  kind: "add" | "del";
  before: string;
  word: string;
  after: string;
}) {
  const line = kind === "add" ? k("diffAdded") : k("diffRemoved");
  const wordTok = kind === "add" ? k("diffAddedWord") : k("diffRemovedWord");
  return (
    <div
      className="whitespace-pre"
      style={{ background: v(line), color: v(k("text")) }}
      data-token={line}
    >
      <C fg={k("inactive")}>{String(n).padStart(4)} </C>
      {kind === "add" ? "+" : "-"}
      {before}
      <C bg={wordTok}>{word}</C>
      {after}
    </div>
  );
}

export const ClaudeCodePreview = memo(function ClaudeCodePreview({
  compact = false,
}: {
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        "space-y-[0.55lh] p-3",
        compact ? "text-[11px] leading-[1.45]" : "text-[13px] leading-[1.55]",
      )}
      style={{ color: v(k("text")) }}
    >
      {!compact && (
        <Box border="claude">
          <C fg={k("claude")}>✻</C>{" "}
          <C fg={k("text")} bold>
            Welcome to Claude Code!
          </C>
          <div>
            <C fg={k("inactive")}> /help for help, /status for your current setup</C>
          </div>
          <div>
            <C fg={k("inactive")}> cwd: ~/ghq/github.com/lnutimura/themes</C>
          </div>
        </Box>
      )}
      <div
        className="whitespace-pre px-[1ch]"
        style={{ background: v(k("userMessageBackground")) }}
        data-token={k("userMessageBackground")}
      >
        <C fg={k("subtle")}>&gt; </C>
        <C fg={k("text")}>make the accent purple and re-export the neovim theme</C>
      </div>
      <div className="whitespace-pre-wrap">
        <C fg={k("text")}>●</C> I'll switch the accent to <C fg={k("claude")}>$purple</C> and
        regenerate the colorscheme.
      </div>
      <Tool name="Read" arg="src/model/tokens.ts">
        <C fg={k("inactive")}>Read 412 lines</C>
      </Tool>
      <Tool name="Update" arg="src/presets/starters.ts">
        <C fg={k("inactive")}>Updated with 1 addition and 1 removal</C>
        <DiffLine n={41} kind="del" before={'    "ui.accent": "'} word="$blue" after={'",'} />
        <DiffLine n={41} kind="add" before={'    "ui.accent": "'} word="$purple" after={'",'} />
      </Tool>
      <Tool name="Bash" arg="pnpm test">
        <C fg={k("success")}>✓ 100 passed</C> <C fg={k("warning")}>1 skipped</C>{" "}
        <C fg={k("error")}>0 failed</C>
      </Tool>
      {!compact && (
        <div className="whitespace-pre">
          <div>
            <C fg={k("success")}>☒</C>{" "}
            <C fg={k("inactive")} style={{ textDecoration: "line-through" }}>
              Change accent
            </C>
          </div>
          <div>
            <C fg={k("text")}>☐ Export Neovim theme</C>
          </div>
        </div>
      )}
      <div className="whitespace-pre">
        <C fg={k("claude")} className="shimmer">
          ✻ Exporting…{" "}
        </C>
        <C fg={k("inactive")}>(12s · ↑ 1.2k tokens · esc to interrupt)</C>
      </div>
      {!compact && (
        <Box border="permission">
          <C fg={k("permission")} bold>
            Bash command
          </C>
          <div>
            <C fg={k("text")}> nvim --headless "+colorscheme themes-gruvbox-dark" +qa</C>
          </div>
          <div className="mt-[0.3lh]">Do you want to proceed?</div>
          <div>
            <C fg={k("permission")}>❯ 1. Yes</C>
          </div>
          <div>
            <C fg={k("text")}> 2. Yes, and don't ask again for nvim commands</C>
          </div>
          <div>
            <C fg={k("text")}> 3. No, and tell Claude what to do differently </C>
            <C fg={k("inactive")}>(esc)</C>
          </div>
        </Box>
      )}
      <Box border="promptBorder">
        <C fg={k("text")}>&gt; /th</C>
        <C token="terminal.cursor" style={{ background: v("terminal.cursor") }}>
          {" "}
        </C>
      </Box>
      <div className="whitespace-pre px-[1ch]">
        <C fg={k("suggestion")}>/theme</C>
        <C fg={k("inactive")}> Change the theme</C>
      </div>
      <div className="flex justify-between whitespace-pre px-[1ch]">
        <C fg={k("autoAccept")}>
          ▸▸ accept edits on <C fg={k("inactive")}>(shift+tab to cycle)</C>
        </C>
        {!compact && <C fg={k("planMode")}>‖ plan mode</C>}
      </div>
    </div>
  );
});
